// =====================================================================================
// INVRT Radar - web console - the one place the operator is told what a coverage answer means
//
// The campaign assignment panel and the access section's Re-link both receive the same
// server answer (scope_applied, delivery_held, scope_token). They share THIS copy so the two
// screens can never tell different stories about the same organisation: coverage is only
// ever called "applied" when the server CONFIRMED it, "held" only when the engine confirmed
// the hold, and anything else says the organisation may be receiving more than was chosen.
// =====================================================================================

import type { RelinkCoverage } from "./AccessSection";

export const TOKEN_COPY: Record<string, string> = {
  assignment_module_subscription_missing: "This organisation has no active subscription to this campaign's module, so nothing was created. Add the module for it first.",
  assignment_module_read_failed: "This organisation's module subscriptions could not be read, so nothing was created. Try it again.",
  access_assignment_version_conflict_or_not_found: "This organisation already holds this campaign, or its access changed. Nothing was changed.",
  access_assignment_invalid_hierarchy_or_interval: "Already assigned for this period",
  assignment_org_not_active: "This organisation is not active, so nothing was created. Reactivate it first.",
  geo_scope_unsupported: "This coverage type is not yet supported for this campaign; nothing was delivered beyond what you chose.",
  assignment_scope_skipped_budget_exhausted: "Assigned, but this request ran out of time before coverage could be applied. Delivery is held until coverage is set from the organisation's campaign row.",
  assignment_scope_unconfirmed_paused_failed: "Coverage was not applied and the hold could not be confirmed, so this organisation may be delivering more than you chose. Check its coverage now.",
  campaign_anchor_failed: "Assigned, but the delivery link could not be attached yet. Use Re-link.",
  assignment_scope_skipped_anchor_unbound: "Assigned and the delivery link was sent, but the platform could not confirm it as attached, so coverage was not applied. Use Re-link to apply your coverage.",
  assignment_scope_stored_unreadable: "The coverage already saved for this organisation could not be read, so it was not assumed to be all coverage. Set coverage from the organisation's campaign row.",
  assignment_scope_engine_pending: "Assigned. Coverage is saved and waiting for the delivery engine to confirm it.",
  geo_version_conflict: "Assigned, but coverage changed while it was being saved. Set coverage again from the organisation's campaign row.",
  assignment_response_invalid: "Assigned, but the server's answer could not be read. Check this organisation's campaign subscriptions.",
  assignment_failed: "An unexpected fault stopped this organisation. Check its access before trying again.",
  assignment_scope_intent_unrecorded: "Not started: the coverage you chose could not be recorded safely, so nothing was created for this organisation. Try it again.",
  assignment_skipped_provider_unavailable: "Not attempted: the delivery partner was unavailable earlier in this request. Nothing was created. Try this organisation again.",
  assignment_skipped_budget_exhausted: "Not attempted: this request ran out of time. Nothing was created. Try this organisation again.",
};

export const UNHELD_WARNING = "Delivery could not be confirmed held, so this organisation may be delivering more than you chose. Use Re-link to apply your coverage, or check it now.";

export interface CoverageNotice {
  tone: "success" | "warning";
  body: string;
}

const GENERIC_UNCONFIRMED = "Coverage could not be confirmed for this organisation.";

/**
 * What a Re-link answer says about coverage, in plain English. `undefined` coverage (the
 * server sent no coverage fields) is UNCONFIRMED, never success: the screen cannot know the
 * saved coverage was applied, so it says so.
 */
export function relinkCoverageNotice(coverage: RelinkCoverage | undefined): CoverageNotice {
  if (coverage === undefined) {
    return { tone: "warning", body: "The delivery link is attached, but the platform did not report whether this organisation's coverage was applied. Check its coverage now." };
  }
  if (coverage.applied) {
    return { tone: "success", body: "The HIGH-INTENT delivery link is active and the saved coverage was applied." };
  }
  const reason = TOKEN_COPY[coverage.token] ?? GENERIC_UNCONFIRMED;
  return coverage.held
    ? { tone: "warning", body: `${reason} Delivery is held until coverage is applied.` }
    : { tone: "warning", body: `${reason} ${UNHELD_WARNING}` };
}
